import { Component, effect, EventEmitter, inject, Injector, Input, OnInit, Output, runInInjectionContext, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatOption, ThemePalette } from '@angular/material/core';
import { FloatLabelType, MatError, MatFormField, MatFormFieldAppearance, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatTooltip, TooltipPosition } from '@angular/material/tooltip';
import { FilterSelectItem } from '../../../models/filter-select-item';
import { PreferredActionType } from '../../../models/preferred-action-type';
import { FieldFilterService } from '../../../services/filters/field-filter.service';

// New-look replacement for app-preferred-action-select. Matches options by ID.
@Component({
  selector: 'app-preferred-action-filter-select',
  templateUrl: './preferred-action-filter-select.component.html',
  styleUrls: ['./preferred-action-filter-select.component.scss'],
  imports: [MatFormField, MatLabel, MatError, MatSelect, MatSelectTrigger, MatOption, MatTooltip, ReactiveFormsModule]
})
export class PreferredActionFilterSelectComponent implements OnInit {

  private readonly filterService = inject(FieldFilterService);
  private readonly injector = inject(Injector);

  private readonly _disabled = signal(false);
  @Input() set disabled(value: boolean) {
    this._disabled.set(value);
  }

  @Input() form: FormGroup | null = null;
  @Input() controlName = 'preferred-action';
  @Input() color: ThemePalette = 'primary';
  @Input() appearance: MatFormFieldAppearance = 'outline';
  @Input() floatLabel: FloatLabelType = 'always';
  @Input() multiple = true;
  @Input() label = 'Preferred Action';
  @Input() placeholder = '';
  @Input() toolTipPosition: TooltipPosition = 'above';
  @Input() selectAllEnabled = true;
  @Input() noneEnabled = false;
  // Read once at init.
  @Input() required = false;
  @Input() typeIds: number[] = [PreferredActionType.Opportunity.valueOf()];
  // Descriptions to keep; empty keeps all.
  @Input() onlyShowOptions: string[] = [];

  @Output() ready = new EventEmitter<FilterSelectItem[]>();
  // Emitted when the panel closes.
  @Output() valueChanged = new EventEmitter<any>();

  options: FilterSelectItem[] = [];
  readonly allOption: FilterSelectItem = { ID: 0, Description: 'All', LinkAssociations: [], Selected: false };
  readonly noneOption: FilterSelectItem = { ID: -1, Description: 'None', LinkAssociations: [], Selected: false };
  readonly control = new FormControl<any>(null);
  private pendingSingleValue: FilterSelectItem | null = null;

  readonly compareById = (a: FilterSelectItem, b: FilterSelectItem) => a?.ID === b?.ID;

  ngOnInit(): void {
    if (this.required) {
      this.control.setValidators([Validators.required]);
    }

    this.form?.addControl(this.controlName, this.control);

    runInInjectionContext(this.injector, () => {
      effect(() => {
        if (this._disabled()) {
          this.control.disable({ emitEvent: false });
        } else {
          this.control.enable({ emitEvent: false });
        }
      });
    });

    this.getOptionData();
  }

  getOptionData() {
    this.filterService.getPreferredActionOptions(this.typeIds.toString()).subscribe((result: FilterSelectItem[]) => {
      const items = (result ?? []).sort((a, b) => a.Description.toLowerCase().localeCompare(b.Description.toLowerCase()));
      this.options = this.onlyShowOptions.length > 0 ? items.filter(i => this.onlyShowOptions.includes(i.Description)) : items;

      this.applyPendingSingleValue();
      this.ready.emit(this.options);
    });
  }

  // Multi mode: [] checks All.
  setValues(values: FilterSelectItem[]) {
    if (!values) {
      return;
    }

    if (values.length == 0) {
      this.setAll(true);
      return;
    }

    this.allOption.Selected = false;
    this.control.setValue(values);
  }

  // Single mode: held until the options load.
  setSingleModeValue(value: FilterSelectItem) {
    if (!value) {
      return;
    }

    if (this.options.length === 0) {
      this.pendingSingleValue = value;
      return;
    }

    this.control.setValue(this.options.find(i => i.ID == value.ID));
  }

  // [allOption] when All is checked.
  getValues(): any {
    if (this.allOption.Selected) {
      return [this.allOption];
    }

    return this.control.value;
  }

  // Multi mode: keeps All in step with the other options.
  onToggleItem(item: FilterSelectItem, selected: boolean) {
    if (!this.multiple || !this.selectAllEnabled) {
      return;
    }

    if (item.ID === this.allOption.ID) {
      this.setAll(selected);
      return;
    }

    const others = this.regularOptions();
    const value: FilterSelectItem[] = (this.control.value ?? []).filter(v => v.ID !== this.allOption.ID);
    const everyChecked = others.every(o => value.some(v => v.ID === o.ID));

    this.allOption.Selected = everyChecked;
    this.control.setValue(everyChecked ? [this.allOption, ...value] : value);
  }

  summary(): string {
    if (this.allOption.Selected) {
      return 'All';
    }

    const selected = this.selectedRegular();

    if (selected.length <= 1) {
      return selected[0]?.Description ?? '';
    }

    return `${selected[0].Description} +${selected.length - 1}`;
  }

  tooltip(): string {
    const selected = this.selectedRegular();
    return this.multiple && selected.length > 1 ? selected.map(o => o.Description).join(', ') : '';
  }

  onSelectionChange() {
    this.valueChanged.emit(this.control.value);
  }

  private setAll(checked: boolean) {
    this.allOption.Selected = checked;
    this.control.setValue(checked ? [this.allOption, ...this.regularOptions()] : []);
  }

  // Every option, plus None when shown.
  private regularOptions(): FilterSelectItem[] {
    return this.noneEnabled ? [...this.options, this.noneOption] : [...this.options];
  }

  private selectedRegular(): FilterSelectItem[] {
    const value = this.control.value;
    return Array.isArray(value) ? value.filter(v => v.ID !== this.allOption.ID) : [];
  }

  private applyPendingSingleValue() {
    if (!this.pendingSingleValue) {
      return;
    }

    const pending = this.pendingSingleValue;
    this.pendingSingleValue = null;
    this.setSingleModeValue(pending);
  }
}
